import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "voice_ideias_token";
const USER_KEY = "voice_ideias_user";
const LOCAL_IDEAS_KEY = "voice_ideias_saved";

// Status types
export const USER_STATUS = {
  TRIAL: "trial",
  ATIVO: "ativo",
  EXPIRADO: "expirado",
  AGUARDANDO_APROVACAO: "aguardando_aprovacao",
  BLOQUEADO: "bloqueado",
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user has active access
  const hasAccess = useCallback(() => {
    if (!user) return false;
    return [USER_STATUS.TRIAL, USER_STATUS.ATIVO].includes(user.status);
  }, [user]);

  // Get status display info
  const getStatusInfo = useCallback(() => {
    if (!user) return null;

    const statusConfig = {
      [USER_STATUS.TRIAL]: {
        label: `Teste gratuito: ${user.days_remaining === 1 ? "resta 1 dia" : `restam ${user.days_remaining} dias`}`,
        color: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        icon: "clock",
      },
      [USER_STATUS.ATIVO]: {
        label: user.plan_name
          ? `Plano ${user.plan_name}`
          : "Assinatura ativa",
        sublabel: user.subscription_end
          ? `Expira em ${new Date(user.subscription_end).toLocaleDateString("pt-BR")}`
          : null,
        color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
        icon: "check",
      },
      [USER_STATUS.EXPIRADO]: {
        label: "Acesso expirado",
        color: "bg-rose-500/20 text-rose-400 border-rose-500/30",
        icon: "x",
      },
      [USER_STATUS.AGUARDANDO_APROVACAO]: {
        label: "Aguardando aprovação",
        color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        icon: "loader",
      },
      [USER_STATUS.BLOQUEADO]: {
        label: "Acesso bloqueado",
        color: "bg-rose-500/20 text-rose-400 border-rose-500/30",
        icon: "ban",
      },
    };

    return statusConfig[user.status] || null;
  }, [user]);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const storedToken = token || localStorage.getItem(TOKEN_KEY);
    return storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
  }, [token]);

  // Migrate local ideas to database
  const migrateLocalIdeas = useCallback(async (authToken) => {
    try {
      const localIdeas = localStorage.getItem(LOCAL_IDEAS_KEY);
      if (!localIdeas) return { migrated: false, count: 0 };

      const ideas = JSON.parse(localIdeas);
      if (!Array.isArray(ideas) || ideas.length === 0) return { migrated: false, count: 0 };

      const ideasToMigrate = ideas.map((idea) => ({
        title: idea.title,
        content: idea.content,
        category: idea.category || "Pessoal",
      }));

      const response = await axios.post(
        `${API}/ideas/migrate`,
        { ideas: ideasToMigrate },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        localStorage.removeItem(LOCAL_IDEAS_KEY);
        return { migrated: true, count: response.data.migrated_count };
      }

      return { migrated: false, count: 0 };
    } catch (error) {
      console.error("Error migrating ideas:", error);
      // Don't fail login if migration fails
      return { migrated: false, count: 0, error };
    }
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          const response = await axios.get(`${API}/auth/verify`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });

          if (response.data.valid) {
            setToken(storedToken);
            setUser(response.data.user);
            // Update stored user with latest status
            localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          }
        }
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      } finally {
        setLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password,
    });

    const { access_token, user: userData } = response.data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    setToken(access_token);
    setUser(userData);

    // Try to migrate local ideas (only if user has access)
    let migration = { migrated: false, count: 0 };
    if ([USER_STATUS.TRIAL, USER_STATUS.ATIVO].includes(userData.status)) {
      migration = await migrateLocalIdeas(access_token);
    }

    return { user: userData, migration };
  }, [migrateLocalIdeas]);

  const register = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
    });

    const { access_token, user: userData } = response.data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    setToken(access_token);
    setUser(userData);

    // Try to migrate local ideas
    const migration = await migrateLocalIdeas(access_token);

    return { user: userData, migration };
  }, [migrateLocalIdeas]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Refresh user status
  const refreshUserStatus = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
    } catch (error) {
      console.error("Error refreshing user status:", error);
    }
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    hasAccess: hasAccess(),
    getStatusInfo,
    login,
    register,
    logout,
    getAuthHeaders,
    refreshUserStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
